/** Tiny DOM builder. Children are always text nodes or elements — never HTML strings (CLAUDE.md). */
export type Child = Node | string | number | null | undefined | false | Child[];
export type Props = Record<string, unknown>;

export const append = (parent: Node, children: Child[]): void => {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) {
      append(parent, c);
      continue;
    }
    parent.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
};

const PROP_KEYS = new Set(['value', 'checked', 'disabled', 'selected', 'readOnly', 'textContent', 'htmlFor', 'className', 'tabIndex']);

export const h = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value === null || value === undefined || value === false) continue;
      if (key === 'class') el.className = String(value);
      else if (key === 'style' && typeof value === 'object') Object.assign(el.style, value);
      else if (key === 'dataset' && typeof value === 'object') Object.assign(el.dataset, value);
      else if (key.startsWith('on') && typeof value === 'function') el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      else if (PROP_KEYS.has(key)) (el as unknown as Record<string, unknown>)[key] = value;
      else el.setAttribute(key, value === true ? '' : String(value));
    }
  }
  append(el, children);
  return el;
};

export const replaceChildren = (el: Element, ...children: Child[]): void => {
  el.replaceChildren();
  append(el, children);
};

/** Short helper for a text span with a class. */
export const span = (cls: string, ...children: Child[]) => h('span', { class: cls }, ...children);
export const div = (cls: string, ...children: Child[]) => h('div', { class: cls }, ...children);
